import { Clear } from '@mui/icons-material';
import { Box, Button, IconButton, Popper } from '@mui/material';
import React, { useState } from 'react';
import { Row } from 'spw-shared/globalComponents';
import WizardAIIcon from 'spw-static/images/wizard-ai-icon.svg';
import { colors } from 'spw-theme';
import styled from 'styled-components';
import CalibrateIcon from 'spw-static/images/calibration-plus-sign-icon.svg';
import AddMeasurementIcon from 'spw-static/images/add-measurement-icon.svg';
import AddSpaceTypeIcon from 'spw-static/images/add-space-type-icon.svg';
import AddSeatCountIcon from 'spw-static/images/add-seat-count-icon.svg';
import { useSelector } from 'react-redux';
import { selectOrgHasAccessToAITools } from 'spw-redux/modules/organization/selectors';

const IconImage = styled.img`
  && {
    width: 20px;
    height: 20px;
  }
`;

const StyledPopper = styled(Box)`
  background-color: #fff;
  color: ${colors.mainGray};
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.2);
  font-size: 14px;
  padding: 24px;
  border-radius: 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FeaturesButton = styled(Button)`
  min-width: 82px;
  border-radius: 4px;
  border: 1px solid ${colors.veryLightGray};
  align-items: center;
  gap: 4px;
  font-size: 14px;
  font-style: normal;
  white-space: nowrap;
  font-weight: 500;
  line-height: 24px;
  height: 33px;

  img {
    width: 18px;
    height: 18px;
  }

  h3 {
    text-transform: none;
  }
`;

const TitleContainer = styled(Row)`
  cursor: pointer;
  min-width: 82px;
`;

const Title = styled.h3`
  margin: 0 0 0 4px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: ${colors.dark};
`;

const Description = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: ${colors.black};
`;

const FeatureTitle = styled(Description)`
  font-weight: 500;
`;

const FeatureContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 6px;

  img {
    width: 24px;
    height: 24px;
  }
`;

const AIFeaturesButton: React.FC<{
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
}> = ({ onClick }) => {
  return (
    <FeaturesButton onClick={onClick}>
      <img src={WizardAIIcon} alt="sparkles" />
      <Title>Wizard AI</Title>
    </FeaturesButton>
  );
};

const AIFeaturesTitle: React.FC<{
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}> = ({ onClick }) => {
  return (
    <TitleContainer alignItems="center" onClick={onClick}>
      <img src={WizardAIIcon} alt="sparkles" />
      <Title
        style={{
          fontSize: 24,
          lineHeight: '32px',
          marginLeft: 8,
        }}
      >
        Wizard AI
      </Title>
    </TitleContainer>
  );
};

const FEATURES_LIST: {
  groupTitle: string;
  features: { icon: string; description: string }[];
}[] = [
  {
    groupTitle: 'Key features include',
    features: [
      {
        icon: CalibrateIcon,
        description: 'Calibration per floor plan',
      },
    ],
  },
  {
    groupTitle: 'Coming soon',
    features: [
      {
        icon: AddMeasurementIcon,
        description: 'Floor USF measurements',
      },
      {
        icon: AddSpaceTypeIcon,
        description: 'Space Type per space',
      },
      {
        icon: AddSeatCountIcon,
        description: 'Seat Count per space',
      },
      {
        icon: AddMeasurementIcon,
        description: 'Space USF measurements',
      },
    ],
  },
];

const AIFeaturesPopup = () => {
  const [titleElement, setTitleElement] = useState<HTMLButtonElement>(null);
  const orgHasAccessToAITools = useSelector(selectOrgHasAccessToAITools);

  return (
    <>
      <AIFeaturesButton onClick={(e) => setTitleElement(e.currentTarget)} />
      <Popper
        open={Boolean(titleElement)}
        anchorEl={titleElement}
        placement="bottom-end"
        disablePortal
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [8, 8],
            },
          },
        ]}
        sx={{ zIndex: 100 }}
      >
        <StyledPopper width={465} boxSizing="border-box">
          <Row alignItems="center" justifyContent="space-between">
            <AIFeaturesTitle />
            <IconButton
              sx={{ padding: 0 }}
              onClick={() => setTitleElement(null)}
            >
              <Clear htmlColor={colors.darkGray} />
            </IconButton>
          </Row>
          <Description>
            Space Plan Wizard is excited to introduce Wizard AI, a magical tool
            designed to automate your workflow.{' '}
          </Description>

          {!orgHasAccessToAITools && (
            <Description>
              NOTE: This functionality has not been activated yet in your
              organization. Please contact your Org Admin.
            </Description>
          )}

          {FEATURES_LIST.map(({ groupTitle, features }) => (
            <FeatureContainer key={groupTitle}>
              <FeatureTitle>{groupTitle}:</FeatureTitle>
              {features.map(({ icon, description }) => (
                <Row alignItems="center" gap={1} key={description}>
                  <IconImage src={icon} alt={description} />{' '}
                  <Description>{description}</Description>
                </Row>
              ))}
            </FeatureContainer>
          ))}
        </StyledPopper>
      </Popper>
    </>
  );
};

export default AIFeaturesPopup;
